import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'

import { Body1 } from '@ui/Typography'
import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'

import { PageQuery, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useDebounceEffect } from '@utils/debounce'

import {
  ScrollToIds,
  useScrollAfterLoad,
  useScrollIntoViewOnPagingEntriesChange,
} from '@hooks/useScrollToElementIds'

import { MultiTierNetworkTable } from './MultiTierNetworkTable'

import styles from './MultiTierNetworkPage.module.scss'

export const MultiTierNetworkPage: FC = () => {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState<string | null>(null)
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string | null>(null)
  useDebounceEffect(() => setDebouncedSearchValue(searchValue), [searchValue], 500)

  const { account } = useAccountReadContext()
  const apiClient = useApiClient(ClientApiClient)

  const introducingBrokerId = account?.clientIntroducingBroker?.id || ''

  const tableQuery = usePaginatedQuery({
    queryKey: queryKeys.ib.network(introducingBrokerId),
    queryFn: (pageQuery) =>
      apiClient.getIBNetworkData(introducingBrokerId, {
        ...pageQuery,
        caller: 'ca',
      }),
  })

  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = useState(false)
  useScrollIntoViewOnPagingEntriesChange(
    ScrollToIds.MultiTierSubIBAccounts,
    isPaginationEntrySelected,
    tableQuery.isLoading,
    setIsPaginationEntrySelected
  )
  useScrollAfterLoad(
    ScrollToIds.MultiTierSubIBAccounts,
    tableQuery.isLoading,
    tableQuery.meta?.pageSize
  )

  const handlePageChange = (pageIndex: number, pageSize?: number) => {
    if (pageSize && pageSize !== tableQuery.meta?.pageSize) {
      setIsPaginationEntrySelected(true)
    }
    tableQuery.setPageQuery!({
      ...tableQuery.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  useEffect(() => {
    if (debouncedSearchValue !== null) {
      tableQuery.setPageQuery!((oldPageQuery: PageQuery) => ({
        ...oldPageQuery,
        pageIndex: 1,
        ca_search_TextSearch: debouncedSearchValue,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue])

  const table = (
    <>
      <MultiTierNetworkTable tableQuery={tableQuery} searchValue={debouncedSearchValue} />
      {tableQuery.meta && (
        <Pagination
          scrollToHeaderId={ScrollToIds.MultiTierSubIBAccounts}
          maxPageSize={100}
          pageData={tableQuery.meta}
          isLoading={tableQuery.isLoading}
          onPageChanged={handlePageChange}
        />
      )}
    </>
  )

  return (
    <div className={styles.networkPage}>
      <PageSection
        title={t('IB.MultiTier.Multi-tier Network')}
        id={ScrollToIds.MultiTierSubIBAccounts}
        searchField={{
          onChange: setSearchValue,
          value: searchValue,
          disabled: false,
          canClear: true,
          renderResult: () => table,
        }}
        infoData={{
          heading: t('IB.MultiTier.Multi-tier Network'),
          direction: 'bottom',
          content: <Body1>{t('IB.MultiTier.This is a break-down of your network')}</Body1>,
        }}
      />
      <div>{table}</div>
    </div>
  )
}
